import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, isEmpty, startCase } from 'lodash';
import qs from 'qs';
import { useRestApi } from 'app/common/hooks';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import * as RequestStatus from 'app/common/hooks/useRestApi/RequestStatus.js';

// Shared footer element for E-Flyer templates
const EFlyerFooterWeb = ({ footer }) => {
  const footerData = useHydrateFooter(footer);
  if (!footerData.ready) {
    return null;
  }

  return (
    <div id={'footer-outer'} className="min-h-32 px-8 md:px-12">
      {isEmpty(footerData.footerText) ? (
        <ul className="flex flex-col md:flex-row text-white min-h-32 justify-between py-6">
          {footerData.locations.map((location, index) => (
            <LocationAddress location={location} key={index} />
          ))}
        </ul>
      ) : (
        <ul className="text-white py-12">
          {footerData.footerText.map((text, index) => (
            <li key={index} className="">
              {text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const LocationAddress = props => {
  const { name, line1, line2, line3, city, state, zip, identifierValue } =
    props.location;

  return (
    <li key={identifierValue} className="my-auto flex-grow mb-4 md:mb-0">
      <div className={'font-bold'}>{name}</div>
      <div>{titleCase(line1)}</div>
      <div>{titleCase(line2)}</div>
      <div>{titleCase(line3)}</div>
      <div>
        {titleCase(city)}, {state} {removeZipSuffix(zip)}
      </div>
    </li>
  );
};

const useHydrateFooter = footer => {
  // track footer fetch and location fetch
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const locationObj = get(footer, 'fields.locations', null);
    const footerTextObj = get(footer, 'fields.footerText', null);

    if (footerTextObj) {
      // text does not need additional fetch
      setReady(true);
    }

    if (
      locationObj &&
      (locationObj.value === undefined || isEmpty(locationObj.value))
    ) {
      // no locations to fetch
      setReady(true);
    }
  }, [footer]);

  const locationIds = useMemo(() => {
    return get(footer, 'fields.locations.value', []);
  }, [footer]);

  const footerText = useMemo(() => {
    return get(footer, 'fields.footerText.value', '');
  }, [footer]);

  const {
    response: locations = [],
    sendCallback: fetchLocations,
    requestStatus: locationRequestStatus
  } = useRestApi('/tenant/resolver/application/locations', undefined, false);

  useEffect(() => {
    if (!isEmpty(locationIds)) {
      fetchLocations({
        params: { locationIds },
        paramsSerializer: p => qs.stringify(p, { indices: false })
      }).catch(() => {});
    }
  }, [fetchLocations, locationIds]);

  useEffect(() => {
    if (locationRequestStatus === RequestStatus.COMPLETE) {
      setReady(true);
    }
  }, [locationRequestStatus]);

  return {
    locations,
    footerText,
    ready: ready
  };
};

/**
 * The footer variables need to be resolved before the PDF starts rendering,
 * so this is used in conjunction with useHydrateFooter
 * @param footerData - the hydrated footer data from useHydrateFooter
 * @returns {Element}
 * @constructor
 */
const EFlyerFooterPDF = ({ footerData }) => {
  let numLocs = get(footerData, 'locations.length', 0);
  if (numLocs === 0) {
    numLocs = 1;
  }
  const eachWidth = 100 / numLocs;

  const pdfStyles = StyleSheet.create({
    outer: {
      color: '#FFFFFF',
      paddingTop: 12,
      paddingBottom: 12,
      paddingLeft: 24,
      paddingRight: 24,
      height: '100%'
    },
    row: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    location: {
      flexGrow: 1,
      width: `${eachWidth}%`,
      padding: 1
    },
    name: {
      fontWeight: 'bold',
      fontSize: 12
    },
    line: {
      fontSize: 12
    },
    footerTextList: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      height: '100%'
    },
    footerTextLine: {
      fontSize: 12,
      marginBottom: 2
    }
  });

  const renderLocations = () => (
    <View style={pdfStyles.row}>
      {footerData.locations.map((loc, idx) => (
        <View key={idx} style={pdfStyles.location}>
          <Text style={pdfStyles.name}>{loc.name}</Text>
          {loc.line1 ? (
            <Text style={pdfStyles.line}>{titleCase(loc.line1)}</Text>
          ) : null}
          {loc.line2 ? (
            <Text style={pdfStyles.line}>{titleCase(loc.line2)}</Text>
          ) : null}
          {loc.line3 ? (
            <Text style={pdfStyles.line}>{titleCase(loc.line3)}</Text>
          ) : null}
          <Text style={pdfStyles.line}>
            {titleCase(loc.city)}, {loc.state} {removeZipSuffix(loc.zip)}
          </Text>
        </View>
      ))}
    </View>
  );

  const renderFooterText = () => (
    <View style={pdfStyles.footerTextList}>
      {Array.isArray(footerData.footerText)
        ? footerData.footerText.map((text, idx) => (
            <Text key={idx} style={pdfStyles.footerTextLine}>
              {text}
            </Text>
          ))
        : null}
    </View>
  );

  const hasFooterText = !isEmpty(footerData.footerText);
  return (
    <View style={pdfStyles.outer}>
      {hasFooterText ? renderFooterText() : renderLocations()}
    </View>
  );
};

function titleCase(str) {
  if (str === null || str === undefined) {
    return '';
  }
  return startCase(str.toLowerCase());
}

function removeZipSuffix(str) {
  if (str === null || str === undefined) {
    return '';
  }
  return str.replace(/-?\d+$/, '');
}

EFlyerFooterWeb.propTypes = {
  // The footer content object (content.fields.footer.value)
  footer: PropTypes.object
};

EFlyerFooterPDF.propTypes = {
  footer: PropTypes.object
};

export { EFlyerFooterWeb, EFlyerFooterPDF, useHydrateFooter };
